import { apiClient } from './client';
import type { UserWordState, Rating, DailyStats, Word, ReviewResult, StudyStatus } from '../types';
import { queueSyncAction, cacheUserState, getCachedUserStates } from '../store/offlineStore';

export interface ReviewPayload {
  word_id: string;
  rating: Rating;
  response_time_ms: number;
}

/** Returns the server-computed review result, or null when offline (queued for sync). */
export async function submitReview(payload: ReviewPayload): Promise<ReviewResult | null> {
  try {
    const { data } = await apiClient.post<ReviewResult>('/progress/review', payload);
    return data;
  } catch {
    await queueSyncAction({
      type: 'review',
      payload: payload as unknown as Record<string, unknown>,
      created_at: Date.now(),
      retries: 0,
    });
    return null;
  }
}

export async function setWordStatus(wordId: string, status: StudyStatus): Promise<UserWordState> {
  const { data } = await apiClient.put<UserWordState>(`/progress/${wordId}/status`, { status });
  return data;
}

export async function bulkSetStatus(wordIds: string[], status: StudyStatus): Promise<number> {
  const { data } = await apiClient.put<{ updated: number }>('/progress/bulk-status', {
    word_ids: wordIds,
    status,
  });
  return data.updated;
}

export async function fetchUserStates(): Promise<UserWordState[]> {
  try {
    const { data } = await apiClient.get<UserWordState[]>('/progress/states');
    for (const state of data) {
      await cacheUserState(state);
    }
    return data;
  } catch {
    return getCachedUserStates();
  }
}

export interface ReviewQueueConfig {
  limit?: number;
  sheet?: string;
  strategy?: 'due' | 'random' | 'weak' | 'new';
}

export async function fetchReviewQueue(config: ReviewQueueConfig = {}): Promise<Word[]> {
  const { data } = await apiClient.get<Word[]>('/review-queue', {
    params: config,
  });
  return data;
}

export async function fetchDailyStats(): Promise<DailyStats> {
  const { data } = await apiClient.get<DailyStats>('/user/dashboard');
  return data;
}
